/** How long a photo upload may take before it counts as failed (K42). */
export const UPLOAD_TIMEOUT_MS = 180_000

/**
 * A signal that aborts after `ms` (calling `onTimeout`), or when the caller's
 * own `signal` does. `timedOut()` tells the two apart; `dispose()` once the
 * request has settled.
 */
export function abortAfter(
  ms: number,
  signal?: AbortSignal | null,
  onTimeout?: () => void
) {
  const controller = new AbortController()
  let timedOut = false
  const abort = () => controller.abort()
  if (signal?.aborted) abort()
  signal?.addEventListener('abort', abort)
  const timer = setTimeout(() => {
    timedOut = true
    abort()
    onTimeout?.()
  }, ms)
  return {
    signal: controller.signal,
    timedOut: () => timedOut,
    dispose: () => {
      clearTimeout(timer)
      signal?.removeEventListener('abort', abort)
    },
  }
}

/**
 * `fetch` that gives up after `ms`: aborts the request and throws a
 * `TimeoutError` (what `AbortSignal.timeout` throws), so a stalled upload fails
 * its push instead of keeping the photos collection syncing (K42). Covers the
 * request until the response's headers arrive, all an upload waits for.
 */
export async function fetchWithTimeout(
  url: RequestInfo | URL,
  init: RequestInit | undefined,
  ms: number
): Promise<Response> {
  const timeout = abortAfter(ms, init?.signal)
  try {
    return await fetch(url, { ...init, signal: timeout.signal })
  } catch (error) {
    if (timeout.timedOut()) {
      throw new DOMException(`no answer after ${ms / 1000} s`, 'TimeoutError')
    }
    throw error
  } finally {
    timeout.dispose()
  }
}
